import React, { FC, useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import PhoneAndroidOutlinedIcon from "@mui/icons-material/PhoneAndroidOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { Colors } from "../../../configs/colors";
import { CustomButton } from "../../atoms";
import { pxToRem } from "../../../util";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import useNfcReader from "../../../hooks/useNfcReader";
import {
  addCustomerNfcTagAction,
  removeCustomerNfcTagAction,
} from "../../../redux/actions/customerAction";
import { ICustomerNfcTagResponse } from "../../../typings";

export interface PrintCustomerTagModalProps {
  open: boolean;
  onClose(): void;
}

const InfoRow: FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  muted?: boolean;
}> = ({ icon, label, value, muted = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "1.375rem 8.75rem 1fr",
      alignItems: "flex-start",
      gap: "0.75rem",
    }}
  >
    <Box sx={{ color: "#7a7a7a", mt: "2px", display: "flex" }}>{icon}</Box>
    <Typography sx={{ color: "#515151", fontWeight: 500, fontSize: pxToRem(14) }}>
      {label}
    </Typography>
    <Typography
      sx={{
        color: muted ? "#8a8f98" : "#2f2f2f",
        fontWeight: muted ? 400 : 500,
        fontSize: pxToRem(14),
      }}
    >
      {value}
    </Typography>
  </Box>
);

const NfcTagRow: FC<{
  tag: ICustomerNfcTagResponse;
  onRemove(nfcTagId: string): void;
  removing: boolean;
}> = ({ tag, onRemove, removing }) => {
  const [confirmPending, setConfirmPending] = useState(false);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString();

  const truncateHash = (hash: string) =>
    hash.length > 16 ? `${hash.slice(0, 8)}…${hash.slice(-6)}` : hash;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        p: "0.75rem 1rem",
        borderRadius: "0.875rem",
        border: "1px solid #ececec",
        bgcolor: "#fafafa",
      }}
    >
      <Box
        sx={{
          height: "2rem",
          width: "2rem",
          borderRadius: "0.625rem",
          bgcolor: `${Colors.PRIMARY}1A`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <PhoneAndroidOutlinedIcon sx={{ color: Colors.PRIMARY, fontSize: "1rem" }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Tooltip title={tag.nfcTagId} placement="top">
            <Typography
              sx={{
                fontSize: pxToRem(13),
                fontWeight: 600,
                color: "#2f2f2f",
                fontFamily: "monospace",
                cursor: "default",
              }}
            >
              {truncateHash(tag.nfcTagId)}
            </Typography>
          </Tooltip>
          {tag.label && (
            <Box
              sx={{
                px: "0.4rem",
                py: "0.1rem",
                borderRadius: "0.375rem",
                bgcolor: `${Colors.SECONDARY}1A`,
                border: `1px solid ${Colors.SECONDARY}33`,
              }}
            >
              <Typography sx={{ fontSize: pxToRem(11), color: Colors.SECONDARY, fontWeight: 600 }}>
                {tag.label}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ fontSize: pxToRem(12), color: "#8a8f98", mt: "0.15rem" }}>
          {formatDate(tag.assignedOn)} · {tag.assignedBy}
        </Typography>
      </Box>

      {confirmPending ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: "0.375rem", flexShrink: 0 }}>
          <Typography sx={{ fontSize: pxToRem(12), color: "#ef4444" }}>Remove?</Typography>
          <IconButton
            size="small"
            disabled={removing}
            onClick={() => {
              onRemove(tag.nfcTagId);
              setConfirmPending(false);
            }}
            sx={{
              height: "1.75rem",
              width: "1.75rem",
              borderRadius: "0.5rem",
              bgcolor: "#fee2e2",
              color: "#ef4444",
              "&:hover": { bgcolor: "#fecaca" },
              "&.Mui-disabled": { bgcolor: "#f5f5f5" },
            }}
          >
            {removing ? (
              <CircularProgress size={12} sx={{ color: "#ef4444" }} />
            ) : (
              <CloseIcon sx={{ fontSize: "0.875rem" }} />
            )}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setConfirmPending(false)}
            sx={{
              height: "1.75rem",
              width: "1.75rem",
              borderRadius: "0.5rem",
              bgcolor: "#f5f5f5",
              color: "#6b7280",
              "&:hover": { bgcolor: "#e5e7eb" },
            }}
          >
            <CloseIcon sx={{ fontSize: "0.875rem" }} />
          </IconButton>
        </Box>
      ) : (
        <IconButton
          size="small"
          onClick={() => setConfirmPending(true)}
          sx={{
            height: "1.75rem",
            width: "1.75rem",
            borderRadius: "0.5rem",
            color: "#9ca3af",
            flexShrink: 0,
            "&:hover": { bgcolor: "#fee2e2", color: "#ef4444" },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      )}
    </Box>
  );
};

const PrintCustomerTagModal: FC<PrintCustomerTagModalProps> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { customer, addNfcTagLoading, removeNfcTagLoading } = useAppSelector(
    (state) => state.customer
  );
  const { user } = useAppSelector((state) => state.auth);
  const scannedNfc = useNfcReader();
  const [isNfcScanning, setIsNfcScanning] = useState(false);

  const loggedInUser = user ? `${user.firstName} ${user.lastName}`.trim() : "staff";

  const nfcTags: ICustomerNfcTagResponse[] = customer?.nfcTags ?? (
    customer?.nfcTagId
      ? [
          {
            id: 0,
            nfcTagId: customer.nfcTagId,
            assignedOn: customer.nfcTagAssignedOn ?? "",
            assignedBy: customer.nfcTagAssignedBy ?? "",
            label: "Primary",
          },
        ]
      : []
  );

  useEffect(() => {
    if (scannedNfc && isNfcScanning && customer?.id) {
      const label = nfcTags.length === 0 ? "Primary" : `Card ${nfcTags.length + 1}`;
      dispatch(
        addCustomerNfcTagAction({
          customerId: customer.id,
          nfcTagId: scannedNfc,
          assignedBy: loggedInUser,
          label,
        })
      ).finally(() => {
        setIsNfcScanning(false);
      });
    }
  }, [scannedNfc]);

  useEffect(() => {
    if (!open) {
      setIsNfcScanning(false);
    }
  }, [open]);

  const handleRemove = (nfcTagId: string) => {
    if (!customer?.id) return;
    dispatch(removeCustomerNfcTagAction({ customerId: customer.id, nfcTagId }));
  };

  const formatPhone = (phone?: string | null) => {
    if (!phone) return "Not assigned";
    const digits = phone.replace(/\D/g, "");
    const ten = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
    const m = ten.match(/^(\d{3})(\d{3})(\d{4})$/);
    return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : phone;
  };

  const customerName =
    [customer?.firstName, customer?.middleName, customer?.lastName].filter(Boolean).join(" ") ||
    "—";

  return (
    <Modal
      open={open}
      onClose={onClose}
      disableScrollLock
      disableRestoreFocus
      sx={{ zIndex: 999 }}
      slotProps={{ backdrop: { sx: { backgroundColor: "rgba(0,0,0,0.5)" } } }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: pxToRem(980),
          maxWidth: "95vw",
          borderRadius: "1.375rem",
          bgcolor: Colors.WHITE,
          boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
          border: "1px solid #d9d9d9",
          overflow: "hidden",
        }}
      >
        {/* ── Modal Header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: "1.75rem",
            py: "1.25rem",
            borderBottom: "1px solid #e7e7e7",
            bgcolor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <Box
              sx={{
                height: "2.75rem",
                width: "2.75rem",
                borderRadius: "1rem",
                bgcolor: `${Colors.PRIMARY}1A`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CreditCardOutlinedIcon sx={{ color: Colors.PRIMARY, fontSize: "1.5rem" }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: pxToRem(22), fontWeight: 600, color: "#2f2f2f", lineHeight: 1 }}
              >
                Customer Tag
              </Typography>
              <Typography sx={{ fontSize: pxToRem(14), color: "#6b7280", mt: "0.25rem" }}>
                Print a bag tag or assign NFC tags to this customer.
              </Typography>
            </Box>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              height: "2.5rem",
              width: "2.5rem",
              borderRadius: "50%",
              border: "1px solid #d9d9d9",
              color: "#6b7280",
              "&:hover": { bgcolor: "#f5f5f5" },
            }}
          >
            <CloseIcon sx={{ fontSize: "1.25rem" }} />
          </IconButton>
        </Box>

        {/* ── Modal Body ── */}
        <Box sx={{ p: "1.5rem", bgcolor: Colors.WHITE }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>

            {/* ── Bag Tag Panel ── */}
            <Box
              sx={{
                borderRadius: "1.25rem",
                border: "1px solid #d9d9d9",
                bgcolor: Colors.WHITE,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  px: "1.25rem",
                  py: "1rem",
                  borderBottom: "1px solid #ececec",
                  bgcolor: "#f8fbff",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    height: "2.75rem",
                    width: "2.75rem",
                    borderRadius: "1rem",
                    bgcolor: `${Colors.SECONDARY}1A`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <SellOutlinedIcon sx={{ color: Colors.SECONDARY, fontSize: "1.25rem" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: pxToRem(18), fontWeight: 600, color: "#2f2f2f" }}>
                    Bag Tag
                  </Typography>
                  <Typography sx={{ fontSize: pxToRem(13), color: "#6b7280" }}>
                    Print a physical customer identification tag.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: "1.25rem", display: "flex", flexDirection: "column", flex: 1, gap: "1rem" }}>
                <Box
                  sx={{
                    borderRadius: "1rem",
                    bgcolor: "#f7f7f7",
                    border: "1px solid #ececec",
                    p: "1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    minHeight: "9.625rem",
                  }}
                >
                  <InfoRow
                    icon={<PersonOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    label="Customer Name"
                    value={customerName}
                  />
                  <InfoRow
                    icon={<TagOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    label="Customer ID"
                    value={customer?.id ? `CUS${customer.id}` : "—"}
                  />
                  <InfoRow
                    icon={<PhoneOutlinedIcon sx={{ fontSize: "1rem" }} />}
                    label="Phone Number"
                    value={formatPhone(customer?.mobilePhone)}
                  />
                </Box>

                <CustomButton
                  sx={{
                    mt: "auto",
                    width: "100%",
                    borderRadius: "1rem",
                    bgcolor: Colors.PRIMARY,
                    color: Colors.WHITE,
                    fontWeight: 600,
                    fontSize: pxToRem(17),
                    py: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    "&:hover": { bgcolor: Colors.PRIMARY_DARK },
                  }}
                  onClick={onClose}
                >
                  <PrintOutlinedIcon sx={{ fontSize: "1.25rem" }} />
                  Print Bag Tag
                </CustomButton>
              </Box>
            </Box>

            {/* ── NFC Tags Panel ── */}
            <Box
              sx={{
                borderRadius: "1.25rem",
                border: "1px solid #d9d9d9",
                bgcolor: Colors.WHITE,
                boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Panel header */}
              <Box
                sx={{
                  px: "1.25rem",
                  py: "1rem",
                  borderBottom: "1px solid #ececec",
                  bgcolor: "#f8fbff",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                }}
              >
                <Box
                  sx={{
                    height: "2.75rem",
                    width: "2.75rem",
                    borderRadius: "1rem",
                    bgcolor: `${Colors.SECONDARY}1A`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <PhoneAndroidOutlinedIcon sx={{ color: Colors.SECONDARY, fontSize: "1.25rem" }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: pxToRem(18), fontWeight: 600, color: "#2f2f2f" }}>
                      NFC Tags
                    </Typography>
                    {nfcTags.length > 0 && (
                      <Box
                        sx={{
                          px: "0.5rem",
                          py: "0.15rem",
                          borderRadius: "0.5rem",
                          bgcolor: `${Colors.PRIMARY}1A`,
                        }}
                      >
                        <Typography sx={{ fontSize: pxToRem(12), fontWeight: 600, color: Colors.PRIMARY }}>
                          {nfcTags.length} assigned
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: pxToRem(13), color: "#6b7280" }}>
                    Program and link NFC tags to this customer.
                  </Typography>
                </Box>
              </Box>

              {/* Panel body */}
              <Box sx={{ p: "1.25rem", display: "flex", flexDirection: "column", flex: 1, gap: "0.75rem" }}>

                {/* Tag list */}
                {nfcTags.length > 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                      maxHeight: "11rem",
                      overflowY: "auto",
                      pr: "0.25rem",
                      "&::-webkit-scrollbar": { width: "4px" },
                      "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                      "&::-webkit-scrollbar-thumb": { bgcolor: "#d1d5db", borderRadius: "4px" },
                    }}
                  >
                    {nfcTags.map((tag) => (
                      <NfcTagRow
                        key={tag.nfcTagId}
                        tag={tag}
                        onRemove={handleRemove}
                        removing={removeNfcTagLoading}
                      />
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      borderRadius: "1rem",
                      bgcolor: "#f7f7f7",
                      border: "1px dashed #d1d5db",
                      p: "1.5rem 1rem",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <PhoneAndroidOutlinedIcon sx={{ color: "#9ca3af", fontSize: "1.75rem" }} />
                    <Typography sx={{ fontSize: pxToRem(13), color: "#9ca3af", textAlign: "center" }}>
                      No NFC tags assigned yet.
                      <br />
                      Scan a card below to get started.
                    </Typography>
                  </Box>
                )}

                {/* Scanning status */}
                {isNfcScanning && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.625rem",
                      px: "1rem",
                      py: "0.625rem",
                      borderRadius: "0.75rem",
                      bgcolor: `${Colors.PRIMARY}0D`,
                      border: `1px solid ${Colors.PRIMARY}33`,
                    }}
                  >
                    <CircularProgress size={14} sx={{ color: Colors.PRIMARY }} />
                    <Typography sx={{ fontSize: pxToRem(13), color: Colors.PRIMARY, fontWeight: 500 }}>
                      Waiting for NFC scan…
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                      size="small"
                      onClick={() => setIsNfcScanning(false)}
                      sx={{ height: "1.5rem", width: "1.5rem", color: Colors.PRIMARY }}
                    >
                      <CloseIcon sx={{ fontSize: "0.875rem" }} />
                    </IconButton>
                  </Box>
                )}

                {/* Add button */}
                <CustomButton
                  disabled={isNfcScanning || addNfcTagLoading}
                  sx={{
                    mt: "auto",
                    width: "100%",
                    borderRadius: "1rem",
                    bgcolor: Colors.PRIMARY,
                    color: Colors.WHITE,
                    fontWeight: 600,
                    fontSize: pxToRem(17),
                    py: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    "&:hover": { bgcolor: Colors.PRIMARY_DARK },
                    "&.Mui-disabled": { bgcolor: "#9ca3af", color: Colors.WHITE },
                  }}
                  onClick={() => setIsNfcScanning(true)}
                >
                  {addNfcTagLoading ? (
                    <CircularProgress size={20} sx={{ color: Colors.WHITE }} />
                  ) : (
                    <AddIcon sx={{ fontSize: "1.25rem" }} />
                  )}
                  {addNfcTagLoading ? "Programming…" : "Add NFC Tag"}
                </CustomButton>
              </Box>
            </Box>

          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default PrintCustomerTagModal;
